import { useT } from "../../../../i18n";
import { formatAgo, latestGenW } from "../../../../utils/energyDevices";
import { KpiTile, formatWatt } from "./EnergyKpi";
import EnergyTimelineChart from "./EnergyTimelineChart";
import SharedPowerLine from "./SharedPowerLine";
import "./EnergyInsight.css";

// 큐브 1대 카드: 실시간 발전 · 소비 · 배터리 + 실측 6h/예측 6h 차트. fog 구성이면 역할 배지와 받음/보냄 줄이 붙는다.
// 머리글을 누르면 접고 편다 — 접힌 상태에는 발전 · 소비 · 배터리 한 줄만 남아 큐브가 많아도 세로가 길어지지 않는다.
// 스마트폴은 이 카드를 쓰지 않는다 — 폴은 같은 타일로 된 PowerGeneration 과 태양광 · 풍력 · 소비 차트(metric="pole")를 따로 쓴다.
// "끊김" 판정은 에너지 데이터(latest.hasData)로만 한다. heartbeat 는 정보 탭 몫 — 두 데이터는 다른 경로로 들어온다.
export default function DeviceEnergyCard({ device, mode, open = true, onToggle, showPlaceholders = false }) {
  const t = useT();
  const latest = device.latest ?? null;
  const offline = !latest || latest.hasData === false;
  const offlineMessage = t("energy.device.disconnected");
  const ago = latest?.timestamp ? formatAgo(latest.timestamp, t) : null;
  const deviceLabel = t(`energy.device.${device.deviceType === "CUBE" ? "CUBE" : "POLE"}`);
  const showRole = mode === "FOG" && device.role;
  const genW = latestGenW(latest, device.deviceType);

  return (
    <div className={`panel ei-panel ei-device${open ? "" : " is-collapsed"}`}>
      <button
        type="button"
        className="ei-device-head ei-device-toggle"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${deviceLabel} ${device.deviceSn} ${t(open ? "energy.device.collapse" : "energy.device.expand")}`}
      >
        {showRole && (
          <span className={`ei-role-badge ${device.role.toLowerCase()}`}>
            {t(`energy.role.${device.role}`)}
          </span>
        )}
        <span className="panel-title">
          {deviceLabel} {device.deviceSn}
        </span>
        <span className="ei-meta">
          <i className={`dt-dot${offline ? " off" : ""}`} />
          {offline && !showPlaceholders ? offlineMessage : ago ?? ""}
        </span>
        <span className="ei-chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {!open ? (
        <div className="ei-device-summary">
          {offline && !showPlaceholders
            ? offlineMessage
            : t("energy.device.summary", {
                gen: formatWatt(genW),
                cons: formatWatt(latest?.currentConsumptionW),
                soc: formatWatt(latest?.currentStoragePercent),
              })}
        </div>
      ) : (
        <>
          {mode === "FOG" && <SharedPowerLine latest={latest} />}
          {offline && !showPlaceholders ? (
            <div className="ei-empty-message">
              {offlineMessage}
              {ago ? ` · ${t("energy.device.lastSeen", { ago })}` : ""}
            </div>
          ) : (
            <div className="ei-kpis">
              <KpiTile label={t("energy.kpi.gen")} value={genW} unit="W" color="#4ad4a8" />
              <KpiTile label={t("energy.kpi.cons")} value={latest?.currentConsumptionW} unit="W" color={device.consumptionPending ? "#FFA500" : "#78aee8"} />
              <KpiTile label={t("energy.kpi.battery")} value={latest?.currentStoragePercent} unit="%" color="#4aa8d8" />
            </div>
          )}
          {/* 끊겨 있어도 지난 6시간 실측은 남아 있으므로 차트는 그대로 둔다 */}
          <EnergyTimelineChart history={device.history} forecast={device.forecast} embedded
            deviceLabel={`${deviceLabel} ${device.deviceSn}`}
            forecastHistory={device.forecastHistory}
            comparisonHour={device.comparisonHour}
            forecastOrigin={device.forecastOrigin} consumptionPending={device.consumptionPending} />
        </>
      )}
    </div>
  );
}
